import React, { Suspense, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResourceType } from '@cotera/api';
import type { ArtifactRendererProps } from './types';
import { registerArtifactRenderer } from './registry';
import {
  AgentArtifactPage,
  AgentPageWithFetchedData,
} from '@cotera/client/app/v1/agent/agent.page';
import { Text } from '@cotera/client/app/components/ui';
import { ChatArtifactAccessGate } from './chat-artifact-access-gate';

type AgentPayload = {
  agentId?: string;
  agentKey?: string;
  isNew?: boolean;
  /**
   * Set on the chat's top-level agent so its @-mentioned documents/agents are
   * surfaced as sibling artifact tabs. Not set on sub-agents opened as their own
   * tabs, so their references aren't expanded recursively.
   */
  expandReferences?: boolean;
};

const AgentArtifactSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="flex-1 min-h-0 overflow-y-auto p-5">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-4/5 rounded bg-coco-accent animate-pulse" />
      </div>
    </div>
  </div>
);

const AgentArtifactRenderer: React.FC<ArtifactRendererProps> = ({
  artifact,
}) => {
  const [searchParams] = useSearchParams();
  const payload = artifact.payload as AgentPayload;
  const agentId = payload?.agentId ?? artifact.artifactId;
  const isNewFromUrl =
    searchParams.get('new') === '1' &&
    searchParams.get('artifactId') === agentId;
  // Captured at mount. An agent opened as a sibling artifact ("Create new agent"
  // in the workflow node picker) registers itself on the conversation, and that
  // round-trip swaps the seeded payload for a bare resource descriptor. Reading
  // the flag live would then drop it and remount the unsaved draft against the
  // (nonexistent) server copy.
  const [isNewFromPayload] = useState(
    () => (artifact.payload as AgentPayload | null)?.isNew === true
  );
  const isNew = isNewFromPayload || isNewFromUrl;
  const expandReferences = payload?.expandReferences === true;

  if (agentId === undefined || agentId === '') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-4">
        <Text.P className="text-muted-foreground">Agent not found</Text.P>
      </div>
    );
  }

  if (isNew) {
    return (
      <AgentArtifactPage
        id={agentId}
        isNew
        expandReferences={expandReferences}
      />
    );
  }

  return (
    <ChatArtifactAccessGate
      type={ResourceType.Agent}
      id={agentId}
      artifactType="agent"
      fallback={<AgentArtifactSkeleton />}
    >
      <Suspense fallback={<AgentArtifactSkeleton />}>
        <AgentPageWithFetchedData
          id={agentId}
          expandReferences={expandReferences}
        />
      </Suspense>
    </ChatArtifactAccessGate>
  );
};

registerArtifactRenderer('agent', AgentArtifactRenderer);
